const _ = require('lodash');
const { idMapWithDims, Constants, isMyKey, enabledDimSettings } = require('./dfpDims');

/** Information about one key-value enabled as a custom dimension in GAM (can be ours or some unrelated) */
class CustomDimDataObject {
	constructor({ id: gamId, name, status, values, active, copyFrom }) {
		if (copyFrom) {
			Object.assign(this, copyFrom);
			return;
		}
		Object.assign(this, {
			gamId,
			name,
			active: active ?? status === 'ACTIVE',
		});
		if (this.isMine) {
			this.values = (values || []).map((o) => ({
				gamId: o.id,
				name: o.name,
				active: o.active ?? o.status === 'ACTIVE',
			}));
		} else { // We don't need info about other key-value values, just how many active values there are.
			this.otherNumValues = _.filter(values, { status: 'ACTIVE' }).length;
		}
	}

	/** Number of 'ACTIVE' key-value values */
	get numActiveValues() {
		return _.filter(this.values, 'active').length + (this.otherNumValues || 0);
	}

	/** Used by getObjectDiff */
	getIdentifier() {
		return this.gamId;
	}

	/** Is it one of our "rlv_g_???_?" custom dimensions, or something unrelated? */
	get isMine() {
		return this.name.startsWith(Constants.DIM_NAME_START);
	}
}

/** The full "state" of the granularity dimensions, this includes
 * 1) the custom dimensions in GAM => .customDimensions
 * 2) The "granularity dimensions" (DfpGranularityDimSetting[]) => .dimSettings
 * 3) Mapping between the numerical ids used in GAM key-values to encode HBA custom dimension keys+values => .idMap */
class DfpGranularityAdsState {
	constructor(params) {
		const { keys = [], values = [], copyFrom } = params || {};
		['idMap', 'dimSettings'].forEach((fld) => {
			this[fld] = params?.[fld] || copyFrom?.[fld];
			if (!this[fld]) {
				throw Error(`'${fld}' not provided`);
			}
		});
		if (copyFrom) {
			this.customDimensions = (copyFrom.customDimensions || [])
				.map((c) => new CustomDimDataObject({ copyFrom: c }));
			return;
		}
		const customKeys = keys.filter((k) => (
			k.reportableType === 'CUSTOM_DIMENSION' && (k.status === 'ACTIVE' || isMyKey(k.name))
		));
		this.customDimensions = customKeys.map((custom) => new CustomDimDataObject({
			...custom,
			values: _.filter(values, { customTargetingKeyId: custom.id }),
		}));
	}

	/** Returns a mapping of necessary GAM key-values using 'dimSettings'
	 * Example: { rlv_g_3448174_2: ['6_8_7_6', '6_8', ...], ... } */
	getNeededKeyVals({ dimSettings }) {
		const enabledDims = enabledDimSettings(dimSettings);
		return _.mapValues(_.groupBy(enabledDims, 'customKeyName'), (arr) => {
			const vals = [''];
			arr.forEach((dim) => {
				if (!dim) {
					return; // Strange but ok, maybe we've removed (!?) some dimension since settings was saved
				}
				const getId = (key, map) => {
					const id = this.idMap[map][key];
					if (!id) {
						throw Error(`Failed finding '${key}' in '${map}'`);
					}
					return id;
				};
				vals.push(...vals.map((s) => dim.values.map((v) => (
					`${s}${s ? '_' : ''}${getId(dim.name, 'keyToId')}_${getId(v, 'valToId')}`
				))).flat());
			});
			return _.filter(_.uniq(vals));
		});
	}

	/** Creates a new DfpGranularityAdsState object from 'dimSettings', along with the necessary "changes" (operations)
	 * that needs to be done in GAM to reflect the change.
	 * The result is an object { newState, changes } where 'changes' is on the form:
	 * {
	 *   vals: { remove: [gamIds], activate: [gamIds], addTo: [{ keyName, valName }] },
     *   keys: { remove: [gamIds], activate: [gamIds], addNames: [keyNames] },
	 * } */
	withDimSettings({ dimSettings, modifyIdMap }) {
		if (this.myCustomDims.find((c) => !c.gamId || c.values.find((v) => !v.gamId))) {
			throw Error('withDimSettings() not supported for preview objects');
		}
		const newState = _.cloneDeep(this);
		if (modifyIdMap) { // Create new ids when needed, used for frontend preview
			newState.idMap = idMapWithDims(newState.idMap, dimSettings);
		}
		const needed = newState.getNeededKeyVals({ dimSettings });
		const changes = {
			vals: { remove: [], activate: [], addTo: [/** { keyName, valName }, ... */] },
			keys: { remove: [], activate: [], addNames: [/** String, ... */] },
		};
		const removeAny = (v, chObj) => {
			if (v.active) {
				v.active = false;
				chObj.remove.push(v.gamId);
			}
		};

		const removeVal = (v) => removeAny(v, changes.vals);
		const removeKey = (v) => removeAny(v, changes.keys);
		const removeKeys = newState.myCustomDims.filter((c) => !needed[c.name]);
		removeKeys.forEach(removeKey);
		removeKeys.forEach((k) => k.values.forEach(removeVal));
		const customDims = _.difference(newState.customDimensions, removeKeys);
		_.entries(needed).forEach(([keyName, valNames]) => {
			let cDim = _.find(customDims, { name: keyName });
			if (!cDim) { // New custom dimension
				cDim = new CustomDimDataObject({ name: keyName, active: true });
				changes.keys.addNames.push(keyName);
				customDims.push(cDim);
			} else if (!cDim.active) { // Activate this custom dimension
				cDim.active = true;
				changes.keys.activate.push(cDim.gamId);
			}
			cDim.values.filter((v) => !valNames.includes(v.name)).forEach(removeVal); // Values to remove
			valNames.forEach((valName) => {
				const val = cDim.values.find((v) => v.name === valName);
				if (!val) { // New value
					cDim.values.push({ name: valName, active: true });
					changes.vals.addTo.push({ keyName: cDim.name, valName });
				} else if (!val.active) {
					val.active = true;
					changes.vals.activate.push(val.gamId);
				}
			});
		});
		newState.customDimensions = customDims;
		return {
			changes: _.values(changes).some((o) => _.find(_.values(o), 'length')) ? changes : null,
			newState,
		};
	}

	/** "Our" GAM key-values */
	get myCustomDims() {
		return _.filter(this.customDimensions, 'isMine');
	}

	/** "Our" active GAM key-values */
	get myActiveCustomDims() {
		return this.customDimensions.filter((d) => d.isMine && d.active);
	}

	/** "Other" GAM key-values */
	get otherCustomDims() {
		return _.filter(this.customDimensions, (d) => !d.isMine);
	}

	/** ALL active GAM key-values */
	get activeDims() {
		return _.filter(this.customDimensions, 'active');
	}

	/** Number of active key-value combinations, not allowed to be > 20k in GAM 360 */
	get comboCount() {
		const arr = _.filter(this.customDimensions, 'numActiveValues');
		return arr.length ? arr.reduce((acc, { numActiveValues }) => (numActiveValues * acc), 1) : 0;
	}
}

DfpGranularityAdsState.Constants = Constants;

module.exports = DfpGranularityAdsState;
